<template>
  <div class="expense-monthly-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Expense Report</h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ formatMoney(grandTotal) }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading expense report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData"
        >Retry</b-button
      >
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-3">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ displayReportTitle }}</span>
              <span class="date-range-text">
                <strong>{{ formatHeaderDate(dtFrom) }}</strong> to
                <strong>{{ formatHeaderDate(dtTo) }}</strong>
              </span>
              <span class="report-summary-text">
                {{ categories.length }}
                {{ categories.length === 1 ? "category" : "categories" }} ·
                {{ totalEntryCount }}
                {{ totalEntryCount === 1 ? "entry" : "entries" }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!categories.length" class="text-center py-5 text-muted">
        <feather-icon icon="InboxIcon" size="32" class="mb-2" />
        <p>No expense entries found for this period.</p>
      </div>

      <div
        v-for="(category, cIndex) in categories"
        :key="category.accountID || cIndex"
        class="category-block"
      >
        <div class="category-heading">
          <div class="category-heading__left">
            <span class="category-num">{{ category.sNo || cIndex + 1 }}</span>
            <h3 class="category-title">
              {{ category.account || "Uncategorized" }}
            </h3>
            <span class="category-meta">
              {{ category.heads.length }}
              {{ category.heads.length === 1 ? "account" : "accounts" }} ·
              {{ category.rowCount }}
              {{ category.rowCount === 1 ? "entry" : "entries" }}
            </span>
          </div>
          <span class="category-total-chip">
            {{ formatMoney(category.categoryTotal) }}
          </span>
        </div>

        <div
          v-for="(head, hIndex) in category.heads"
          :key="head.accountID || hIndex"
          class="account-block"
        >
          <div class="account-heading">
            <div class="account-heading__left">
              <span class="account-index">
                {{ cIndex + 1 }}.{{ hIndex + 1 }}
              </span>
              <span class="account-name">
                {{ head.account || "Unnamed account" }}
              </span>
              <span class="account-count">
                {{ head.rows.length }}
                {{ head.rows.length === 1 ? "entry" : "entries" }}
              </span>
            </div>
            <span class="account-heading__total">
              {{ formatMoney(head.total) }}
            </span>
          </div>

          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 5%">#</th>
                <th style="width: 11%">Date</th>
                <th style="width: 31%">Details</th>
                <th style="width: 14%">Paid To</th>
                <th style="width: 13%">Paid By</th>
                <th style="width: 12%">Voucher</th>
                <th class="text-right" style="width: 14%">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rIndex) in head.rows"
                :key="row.detailID || rIndex"
              >
                <td class="text-center font-weight-bold">{{ rIndex + 1 }}</td>
                <td>{{ row.dateStr || formatHeaderDate(row.date) }}</td>
                <td class="font-weight-bold">{{ row.details || "—" }}</td>
                <td>{{ row.paidTo || "—" }}</td>
                <td>{{ row.paidBy || "—" }}</td>
                <td>{{ row.voucherNumStr || "—" }}</td>
                <td class="text-right font-weight-bold">
                  {{ formatMoney(row.amount) }}
                </td>
              </tr>
              <tr v-if="!head.rows.length">
                <td colspan="7" class="text-center text-muted py-3">
                  No entries in this account
                </td>
              </tr>
              <tr class="account-total-row">
                <td colspan="6" class="text-right font-weight-bold">
                  {{ head.account || "Account" }} Total
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatMoney(head.total) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!category.heads.length" class="empty-heads">
          No accounts found in this category
        </div>

        <div class="category-total-row">
          <span>{{ category.account || "Category" }} Total</span>
          <span>{{ formatMoney(category.categoryTotal) }}</span>
        </div>
      </div>

      <div v-if="categories.length" class="grand-total-bar">
        <span>Grand Total</span>
        <span>{{ formatMoney(grandTotal) }}</span>
      </div>

      <!-- Chart at bottom -->
      <div v-if="showChart" class="chart-card mt-4">
        <div class="chart-card__head">
          <h5 class="chart-title mb-0">Daily Expense Total</h5>
          <span class="chart-total-hint"
            >Period total: <strong>{{ formatMoney(grandTotal) }}</strong></span
          >
        </div>
        <div class="chart-wrap">
          <apexchart
            :key="chartRenderKey"
            type="bar"
            height="320"
            width="100%"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>
      </div>

      <div class="page-footer no-print">
        Generated on {{ currentDateTime }}
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import axios from "axios";

export default {
  name: "ExpenseMonthlyCategoryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      error: null,
      categories: [],
      chartPoints: [],
      reportTitle: "",
      grandTotal: 0,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      dtFrom: "",
      dtTo: "",
      accountIDs: "",
      coaAccountIDs: "",
      chartRenderKey: 0,
      chartSeries: [{ name: "Expense", data: [] }],
      chartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          animations: { enabled: false },
        },
        xaxis: { categories: [] },
      },
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    showChart() {
      return Array.isArray(this.chartPoints) && this.chartPoints.length > 0;
    },
    displayReportTitle() {
      const title = String(this.reportTitle || "").trim();
      if (!title) return "Expense Report";
      // API titles often already embed the period ("Expense report 01-Aug-2026 to 31-Oct-2026");
      // the header renders the formatted range separately, so drop the duplicate.
      const cleaned = title
        .replace(
          /\s*\b\d{1,2}[-/ ](?:[A-Za-z]{3,}|\d{1,2})[-/ ]\d{2,4}\s*(?:to|-|–|—)\s*\d{1,2}[-/ ](?:[A-Za-z]{3,}|\d{1,2})[-/ ]\d{2,4}\b\s*$/i,
          ""
        )
        .trim();
      return cleaned || "Expense Report";
    },
    totalEntryCount() {
      return this.categories.reduce((sum, c) => sum + (c.rowCount || 0), 0);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const q = this.$route.query || {};
      this.db = q.db || this.$store.state.userData.db;
      this.cID = Number(q.cID || q.cId || this.$store.state.userData.cId || 0);
      this.dtFrom = q.dtFrom || "";
      this.dtTo = q.dtTo || "";
      this.accountIDs = q.accountIDs || q.accID || "";
      this.coaAccountIDs = q.coaAccountIDs || q.coa || "";
      this.applyBranchFallback();
      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }
      this.loadCampusInfo();
      this.loadReportData();
    },

    applyBranchFallback() {
      const branches = this.$store.state.branches || [];
      const currentBranch =
        branches.find((b) => b.id == this.cID) ||
        this.$store.state.currentBranch ||
        {};
      this.campusName =
        currentBranch.organizationName ||
        currentBranch.name ||
        currentBranch.branch ||
        "MY SKOOL";
      this.campusBranch = currentBranch.branch || "";
      this.schoolLogo = currentBranch.logo || null;
    },

    async loadCampusInfo() {
      try {
        const res = await this.$store.dispatch("get", {
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.db +
            "&cId=" +
            this.cID,
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
        const match = list.find((c) => Number(c.id) === Number(this.cID));
        if (match) {
          this.campusName =
            match.organizationName ||
            match.orgName ||
            match.name ||
            this.campusName;
          this.campusBranch =
            this.cID == 0 ? "All Campuses" : match.branch || match.name || "";
          this.schoolLogo = match.logo || this.schoolLogo;
        }
      } catch (e) {
        this.applyBranchFallback();
      }
    },

    buildChart(points) {
      const list = Array.isArray(points) ? points : [];
      const many = list.length > 14;
      const categories = list.map((p) => this.formatChartLabel(p));
      const values = list.map((p) => Number(p.total) || 0);

      this.chartSeries = [{ name: "Expense", data: values }];
      this.chartOptions = {
        chart: {
          id: "expense-daily-" + Date.now(),
          type: "bar",
          toolbar: { show: false },
          animations: { enabled: false },
          fontFamily: "Segoe UI, sans-serif",
          foreColor: "#2d3436",
          width: "100%",
          height: 320,
        },
        colors: ["#2c3e50"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: many ? "72%" : "55%",
            borderRadius: 3,
            dataLabels: { position: "top" },
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -14,
          style: {
            fontSize: many ? "8px" : "10px",
            fontWeight: 700,
            colors: ["#2c3e50"],
          },
          formatter(val) {
            const n = Number(val) || 0;
            if (!n) return "";
            if (n >= 1000) {
              const k = n / 1000;
              return (Number.isInteger(k) ? k : k.toFixed(1)) + "k";
            }
            return String(n);
          },
        },
        stroke: { show: false },
        xaxis: {
          categories,
          labels: {
            rotate: many ? -55 : -35,
            rotateAlways: true,
            style: {
              fontSize: many ? "9px" : "10px",
              fontWeight: 600,
              colors: "#4b5563",
            },
          },
          axisBorder: { show: true, color: "#d1d5db" },
          axisTicks: { show: true, color: "#d1d5db" },
        },
        yaxis: {
          labels: {
            style: { fontSize: "10px", fontWeight: 600, colors: "#4b5563" },
            formatter(val) {
              const n = Number(val) || 0;
              return n >= 1000 ? n / 1000 + "K" : String(n);
            },
          },
        },
        fill: { opacity: 1, type: "solid" },
        tooltip: {
          y: {
            formatter: (val) => this.formatMoney(val),
          },
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: { top: 16, right: 8, bottom: 0, left: 8 },
        },
        legend: { show: false },
      };
      this.chartRenderKey += 1;
    },

    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) baseUrl += "/";

        const params = { db: this.db, cID: this.cID };
        if (this.dtFrom) params.dtFrom = this.dtFrom;
        if (this.dtTo) params.dtTo = this.dtTo;
        if (this.accountIDs && String(this.accountIDs) !== "0") {
          params.accountIDs = this.accountIDs;
        }
        if (this.coaAccountIDs && String(this.coaAccountIDs) !== "0") {
          params.coaAccountIDs = this.coaAccountIDs;
        }

        const headers = {
          Authorization: `Bearer ${this.$store.state.userData.token}`,
          accept: "application/json",
        };

        let response;
        try {
          response = await axios.get(
            `${baseUrl}expenses/GetMonthlyCategoryReport`,
            { params, headers }
          );
        } catch (e1) {
          response = await axios.get(
            `${baseUrl}Expenses/GetMonthlyCategoryReport`,
            { params, headers }
          );
        }

        const payload = response.data;
        if (!payload || payload.status !== "success") {
          this.error =
            (payload && payload.message) ||
            "Failed to load expense report data.";
          this.categories = [];
          this.chartPoints = [];
          return;
        }

        const data = payload.data || {};
        this.categories = this.normalizeCategories(data.categories);
        this.chartPoints = Array.isArray(data.chart) ? data.chart : [];
        this.reportTitle = data.reportTitle || "";
        this.grandTotal =
          Number(data.grandTotal) ||
          this.categories.reduce((sum, c) => sum + c.categoryTotal, 0);
        if (data.dtFrom) this.dtFrom = data.dtFrom;
        if (data.dtTo) this.dtTo = data.dtTo;

        this.buildChart(this.chartPoints);
        this.$nextTick(() => {
          setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
        });
      } catch (e) {
        console.error(e);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
        this.categories = [];
        this.chartPoints = [];
      } finally {
        this.loading = false;
      }
    },

    normalizeCategories(list) {
      const source = Array.isArray(list) ? list : [];
      return source
        .filter((category) => category && typeof category === "object")
        .map((category, cIndex) => {
          const heads = Array.isArray(category.heads)
            ? category.heads
            : [];
          const normalizedHeads = heads
            .filter((head) => head && typeof head === "object")
            .map((head) => {
              const rows = Array.isArray(head.rows) ? head.rows : [];
              const total =
                head.total != null
                  ? Number(head.total) || 0
                  : rows.reduce(
                      (sum, row) => sum + (Number(row && row.amount) || 0),
                      0
                    );
              return { ...head, rows, total };
            })
            .filter((head) => head.rows.length || head.total);

          // Older payloads returned rows directly on the category; keep those visible.
          if (!normalizedHeads.length && Array.isArray(category.rows)) {
            const rows = category.rows;
            if (rows.length) {
              normalizedHeads.push({
                accountID: category.accountID,
                account: category.account,
                rows,
                total: rows.reduce(
                  (sum, row) => sum + (Number(row && row.amount) || 0),
                  0
                ),
              });
            }
          }

          const categoryTotal =
            category.categoryTotal != null
              ? Number(category.categoryTotal) || 0
              : normalizedHeads.reduce((sum, head) => sum + head.total, 0);

          return {
            ...category,
            sNo: category.sNo || cIndex + 1,
            categoryTotal,
            heads: normalizedHeads,
            rowCount: normalizedHeads.reduce(
              (sum, head) => sum + head.rows.length,
              0
            ),
          };
        });
    },

    formatChartLabel(point) {
      if (!point) return "";
      if (point.date) {
        const d = new Date(point.date);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          });
        }
      }
      const label = String(point.dateLabel || "").trim();
      if (!label) return "";
      const m = label.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (m) {
        const day = Number(m[2]);
        const monthIdx = Number(m[1]) - 1;
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        if (monthIdx >= 0 && monthIdx < 12) {
          return day + " " + months[monthIdx];
        }
      }
      return label;
    },

    formatMoney(value) {
      const num = Number(value) || 0;
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(num);
    },

    formatHeaderDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    print() {
      window.dispatchEvent(new Event("resize"));
      setTimeout(() => window.print(), 400);
    },

    close() {
      window.close();
      this.$router.push({ name: "expenses" }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.expense-monthly-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", "Segoe UI", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: #ffffff;
  padding: 2.5rem 2.25rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 3px solid #1f2937;
  padding-bottom: 16px;

  .school-logo-img {
    height: 100px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.75rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
  }

  .report-info-line {
    font-size: 1rem;
    color: #4b5563;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #d1d5db;
      padding-right: 14px;
      margin-right: 14px;
      color: #1f2937;
    }

    .report-summary-text {
      font-size: 0.85rem;
      font-weight: 600;
      color: #6b7280;
      border-left: 2px solid #d1d5db;
      padding-left: 14px;
      margin-left: 14px;
    }
  }
}

.chart-card {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 1rem 0.5rem;
}

.chart-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.chart-title {
  font-weight: 800;
  font-size: 0.9rem;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.chart-total-hint {
  font-size: 0.82rem;
  color: #6b7280;

  strong {
    color: #111827;
  }
}

.chart-wrap {
  width: 100%;
  min-height: 320px;
  position: relative;
}

.chart-wrap ::v-deep .apexcharts-canvas {
  margin: 0 auto;
}

.category-block {
  margin-bottom: 1.75rem;
  page-break-inside: auto;
}

.category-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0.95rem;
  background: #1f2937;
  color: #ffffff !important;
  border-radius: 8px 8px 0 0;
}

.category-heading__left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.category-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff !important;
  font-weight: 800;
  font-size: 0.8rem;
}

.category-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #ffffff !important;
  letter-spacing: 0.02em;
}

.category-meta {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.category-total-chip {
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff !important;
  font-weight: 800;
  font-size: 0.85rem;
  white-space: nowrap;
}

.account-block {
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  page-break-inside: auto;
}

.account-block + .account-block {
  margin-top: -1px;
}

.account-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.85rem;
  background: #eef2f7;
  border-top: 1px solid #d1d5db;
}

.account-heading__left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.account-index {
  font-size: 0.7rem;
  font-weight: 800;
  color: #1f2937;
  background: #d7dee8;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  white-space: nowrap;
}

.account-name {
  font-size: 0.85rem;
  font-weight: 800;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  overflow-wrap: anywhere;
}

.account-count {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
}

.account-heading__total {
  font-size: 0.85rem;
  font-weight: 800;
  color: #1f2937;
  white-space: nowrap;
}

.empty-heads {
  border: 1px solid #d1d5db;
  border-top: none;
  padding: 0.75rem;
  text-align: center;
  font-size: 0.85rem;
  color: #9ca3af;
}

.category-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.95rem;
  background: #dbe3ee;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 8px 8px;
  font-weight: 800;
  font-size: 0.9rem;
  color: #1f2937;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: none;

  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 0.55rem 0.7rem;
    font-size: 0.875rem;
    color: #1f2937;
  }

  thead {
    background: #1f2937 !important;

    th {
      color: #ffffff !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.72rem;
      letter-spacing: 0.04em;
    }
  }

  tbody tr:nth-child(even) {
    background: #f9fafb;
  }

  .account-total-row td {
    background: #f4f7fb !important;
    font-weight: 700;
  }
}

.grand-total-bar {
  margin-top: 1.1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.1rem;
  background: #1f2937;
  color: #fff !important;
  border-radius: 8px;
  font-weight: 800;
  font-size: 1.05rem;
}

.page-footer {
  text-align: center;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #eee;
  color: #9ca3af;
  font-size: 0.82rem;
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  .expense-monthly-report {
    background: #fff !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-header-modern {
    border-bottom: 3px solid #000 !important;
    margin-bottom: 12px !important;
    padding-bottom: 10px !important;

    .school-logo-img {
      height: 80px !important;
    }

    .school-name-full {
      font-size: 1.35rem !important;
      color: #000 !important;
    }
  }

  .chart-card {
    background: #fff !important;
    border: 1px solid #bbb !important;
    border-radius: 0 !important;
    padding: 8px 6px 2px !important;
    margin-top: 14px !important;
    break-inside: avoid;
    page-break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .chart-wrap {
    min-height: 280px !important;
  }

  .chart-wrap ::v-deep .apexcharts-canvas,
  .chart-wrap ::v-deep svg {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .category-heading {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: #1f2937 !important;
    color: #fff !important;
    break-after: avoid;
  }

  .category-title,
  .category-num,
  .category-total-chip {
    color: #fff !important;
  }

  .category-meta {
    color: rgba(255, 255, 255, 0.75) !important;
  }

  .category-block {
    page-break-inside: auto;
  }

  .account-block {
    break-inside: auto;
  }

  .account-heading {
    background: #e8edf4 !important;
    border-color: #000 !important;
    break-after: avoid;
    page-break-after: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    .account-index {
      background: #cfd8e3 !important;
      color: #000 !important;
    }

    .account-name,
    .account-heading__total {
      color: #000 !important;
    }
  }

  .account-total-row td,
  .category-total-row {
    background: #e8edf4 !important;
    color: #000 !important;
    border-color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .category-total-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;

    th,
    td {
      padding: 4px 6px !important;
      border-color: #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .account-total-row td {
      background: #eee !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .grand-total-bar {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: #1f2937 !important;
    color: #fff !important;
    break-inside: avoid;
  }
}
</style>
